import type { VehicleCardDto, VehicleEnvironmentDto } from '@/lib/sanity/dto';
import { stegaClean } from '@/lib/utils/stega';
import {
    VEHICLE_BODY_TYPE_LABELS,
    VEHICLE_CONDITION_LABELS,
    VEHICLE_FUEL_LABELS,
    VEHICLE_OFFER_CONDITIONS,
    VEHICLE_PRICE_ON_REQUEST,
    VEHICLE_TRANSMISSION_LABELS,
} from './vehicle.constants';
import {
    formatAmount,
    formatDecimal,
    formatMileage,
    formatMonthYear,
    formatPower,
    isNumber,
    monthIndexOf,
    yearOf,
} from './vehicle.format';
import { vehicleHref } from './vehicle.path';
import type { VehicleAvailability, VehicleCardModel, VehicleSpec } from './vehicle.types';

/** The fields the card, the detail page and the feed all derive from. */
type VehicleBase = Omit<VehicleCardDto, 'image' | 'imageCount' | 'hasFinancing'>;

const FINANCE_NOTE = 'Finanzierung möglich';

/** Reserved beats a future availability date; everything else stands on the lot. */
export function vehicleAvailability(vehicle: VehicleBase, today = new Date()): VehicleAvailability {
    if (vehicle.reserved) return { state: 'reserved', label: 'Reserviert' };

    const from = vehicle.availableFrom;
    if (from && from > today.toISOString().slice(0, 10)) {
        return { state: 'fromDate', label: `Verfügbar ab ${formatMonthYear(from)}` };
    }

    return { state: 'available', label: 'Sofort verfügbar' };
}

/** Photo badges: the drive, then the kind of offer. Condition terms belong to the chips. */
export function vehicleBadges(vehicle: VehicleBase): string[] {
    const fuel = vehicle.fuel && vehicle.fuel !== 'other' ? VEHICLE_FUEL_LABELS[vehicle.fuel] : undefined;
    const offer =
        vehicle.condition && VEHICLE_OFFER_CONDITIONS.includes(vehicle.condition)
            ? VEHICLE_CONDITION_LABELS[vehicle.condition]
            : undefined;

    return [fuel, offer].filter((badge): badge is string => Boolean(badge));
}

/**
 * Model text and highlights from the DMS. The export leaves both empty so far,
 * so the line falls back to condition and body type rather than staying blank.
 */
export function vehicleSubtitle(vehicle: VehicleBase): string {
    const authored = [vehicle.modelText, ...(vehicle.highlights ?? [])].filter(Boolean);
    if (authored.length) return authored.join(' · ');

    return [
        vehicle.condition && VEHICLE_CONDITION_LABELS[vehicle.condition],
        vehicle.bodyType && VEHICLE_BODY_TYPE_LABELS[vehicle.bodyType],
    ]
        .filter(Boolean)
        .join(' · ');
}

/** Cents only where the price has them, so the page shows exactly the amount the ads feed carries. */
export function vehiclePriceText(price?: number): string {
    return isNumber(price) ? formatAmount(price) : VEHICLE_PRICE_ON_REQUEST;
}

/** The manufacturer's recommended price — page, JSON-LD and feed show it for new cars only. */
export function vehicleMsrp(vehicle: { condition?: VehicleCardDto['condition']; msrp?: number }): number | undefined {
    return vehicle.condition === 'new' && isNumber(vehicle.msrp) && vehicle.msrp > 0 ? vehicle.msrp : undefined;
}

/**
 * Model year for the feed and the JSON-LD. The DMS leaves it empty so far, so the
 * construction year stands in (decided 2026-10-13, pending the customer's answer).
 */
export function vehicleYear(vehicle: { modelYear?: number; constructionYear?: number }): number | undefined {
    return vehicle.modelYear ?? vehicle.constructionYear;
}

export function vehicleSpecs(vehicle: VehicleBase): VehicleSpec[] {
    const specs: VehicleSpec[] = [
        { icon: 'registration', value: formatMonthYear(vehicle.firstRegistration) },
        { icon: 'mileage', value: isNumber(vehicle.mileage) ? formatMileage(vehicle.mileage) : '' },
        { icon: 'power', value: formatPower(vehicle.powerKw, vehicle.powerPs) },
        { icon: 'transmission', value: vehicle.transmission ? VEHICLE_TRANSMISSION_LABELS[vehicle.transmission] : '' },
    ];

    return specs.filter((spec) => spec.value);
}

function withUnit(value: number | undefined, unit: string): string | undefined {
    return isNumber(value) ? `${formatDecimal(value)} ${unit}` : undefined;
}

function classPart(label: string, value?: string): string | undefined {
    return value ? `${label} ${value}` : undefined;
}

/**
 * Pkw-EnVKV values of a card's short line, per drive (the design's ENVKV_CARD_LINE).
 * A value the export leaves empty drops out rather than showing a gap.
 */
export function vehicleEnvkvParts(vehicle: VehicleBase): string[] {
    const env: VehicleEnvironmentDto = vehicle.environment ?? {};
    let parts: (string | undefined)[];

    if (vehicle.fuel === 'electric') {
        parts = [
            withUnit(env.powerCombined, 'kWh/100 km (komb.)'),
            withUnit(env.co2Combined, 'g CO₂/km (komb.)'),
            classPart('CO₂-Klasse', env.co2Class),
        ];
    } else if (vehicle.fuel === 'pluginHybrid') {
        parts = [
            withUnit(env.weightedFuel, 'l/100 km'),
            withUnit(env.weightedPower, 'kWh/100 km (gewichtet komb.)'),
            withUnit(env.weightedCo2, 'g CO₂/km'),
            classPart('CO₂-Klasse', env.co2ClassWeighted ?? env.co2Class),
        ];
    } else {
        parts = [
            withUnit(env.fuelCombined, 'l/100 km (komb.)'),
            withUnit(env.co2Combined, 'g CO₂/km (komb.)'),
            // Older cars carry the NEFZ efficiency class instead of a CO₂ class.
            classPart('CO₂-Klasse', env.co2Class) ?? classPart('Effizienzklasse', env.efficiencyClass),
        ];
    }

    return parts.filter((part): part is string => Boolean(part));
}

/** DTO → card. Cleans stega once, so every label, key and URL below is plain. */
export function toVehicleCard(dto: VehicleCardDto): VehicleCardModel {
    const vehicle = stegaClean(dto);

    return {
        id: vehicle._id,
        href: vehicleHref(vehicle),
        title: vehicle.title ?? [vehicle.make, vehicle.model].filter(Boolean).join(' '),
        subtitle: vehicleSubtitle(vehicle),
        image: vehicle.image?.asset ? vehicle.image : null,
        imageCount: vehicle.imageCount ?? 0,
        badges: vehicleBadges(vehicle),
        availability: vehicleAvailability(vehicle),
        priceText: vehiclePriceText(vehicle.price),
        financeNote: vehicle.hasFinancing && isNumber(vehicle.price) ? FINANCE_NOTE : undefined,
        specs: vehicleSpecs(vehicle),
        envkvParts: vehicleEnvkvParts(vehicle),
        facets: {
            make: vehicle.make,
            model: vehicle.model,
            condition: vehicle.condition,
            bodyType: vehicle.bodyType,
            fuel: vehicle.fuel,
            transmission: vehicle.transmission,
            price: vehicle.price,
            mileage: vehicle.mileage,
            registrationYear: yearOf(vehicle.firstRegistration),
            registration: monthIndexOf(vehicle.firstRegistration),
            listedAt: vehicle.firstImportedAt,
        },
    };
}
